import Cookies from "js-cookie"

import { render, html, nothing } from "lit-html"

import Modal from "./js/generic/modal"
import { renderImageGallery } from "./js/embeds/image-gallery"
import { addToastMessage } from "./js/generic/messages"
import { renderNestedDocumentFormInput } from "./js/inputs"
import { confirmDeleteAttachment } from "./js/inputs/nested-documents"

const csrfToken = Cookies.get("csrftoken")

const bindDownloadButton = ($btn) => {
  $btn.addEventListener("click", (e) => {
    e.preventDefault()

    const downloadFile = (url) => {
      if ($btn.dataset.url === undefined) {
        $btn.dataset.url = url
      }

      var link = document.createElement("a")
      link.href = url
      link.download = $btn.dataset.name
      link.click()
      link.remove()
    }

    if ($btn.dataset.url) {
      downloadFile($btn.dataset.url)
    } else {
      $btn.querySelector("svg").classList.add("fa-bounce")
      $btn.disabled = true
      fetch($btn.dataset.download, {
        method: "GET",
        credentials: "same-origin",
      })
        .then((res) => {
          if (!res.ok) {
            res.text().then((text) => {
              throw new Error(text)
            })
          }

          if (res.headers.get("content-type").includes("json")) {
            res.json().then((data) => {
              downloadFile(data.url)
            })
          } else {
            res.blob().then((blob) => {
              downloadFile(window.URL.createObjectURL(blob))
            })
          }
        })
        .catch((err) => {
          console.error(err)
          addToastMessage(
            "There is an error when downloading this file",
            "error"
          )
        })
        .finally(() => {
          $btn.querySelector("svg").classList.remove("fa-bounce")
          $btn.disabled = false
        })
    }
  })
}

const bindShareButton = ($btn) => {
  $btn.addEventListener("click", (e) => {
    e.preventDefault()
    const shareLink = $btn.dataset.share
    const shareUrl = shareLink.startsWith("/")
      ? `${window.location.origin}${shareLink}`
      : `${window.location.origin}${window.location.pathname}${shareLink}`
    navigator.clipboard.writeText(shareUrl)
    addToastMessage("Copied share link to clipboard")
  })
}

const bindHighlightButton = ($attachments, $btn) => {
  const onHighlightDocument = () => {
    fetch($btn.dataset.highlight, {
      method: "POST",
      body: JSON.stringify({
        assignment: $btn.dataset.assignmentId,
        document: $btn.dataset.documentId,
      }),
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": csrfToken,
      },
    })
      .then((res) => {
        if (res.ok) {
          $attachments.dispatchEvent(new CustomEvent("refresh"))
        } else {
          return res.text().then((text) => {
            throw new Error(text)
          })
        }
      })
      .catch((error) => {
        console.error(error)
        addToastMessage("There was an error highlighting the document", "error")
      })
  }

  $btn.addEventListener("click", (e) => {
    e.preventDefault()
    showHighlightModal(onHighlightDocument, true)
  })
}

const bindUnhighlightButton = ($attachments, $btn) => {
  const onUnhighlightDocument = () => {
    fetch($btn.dataset.unhighlight, {
      method: "DELETE",
      body: JSON.stringify({
        assignment: $btn.dataset.assignmentId,
        document: $btn.dataset.documentId,
      }),
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": csrfToken,
      },
    })
      .then((res) => {
        if (res.ok) {
          $attachments.dispatchEvent(new CustomEvent("refresh"))
        } else {
          return res.text().then((text) => {
            throw new Error(text)
          })
        }
      })
      .catch((error) => {
        console.error(error)
        addToastMessage(
          "There was an error unhighlighting the document",
          "error"
        )
      })
  }

  $btn.addEventListener("click", (e) => {
    e.preventDefault()
    showHighlightModal(onUnhighlightDocument, false)
  })
}

const showHighlightModal = (onConfirmAction, highlight) => {
  const modalMount = document.getElementById("modal-mount")

  const onClose = () => {
    render(nothing, modalMount)
  }

  const onConfirm = () => {
    onClose()
    onConfirmAction()
  }

  render(
    Modal({
      onClose,
      modalBackgroundClass: "modal-background bg-white bg-opacity-90",
      children: html`
        <div
          class="modal-content mx-auto max-w-[660px] shadow-[0px_0px_4px_0px_rgba(203,211,219,0.55)] bg-white p-8 text-black"
        >
          <h2 class="mb-8 text-xl font-bold">
            ${highlight
              ? "Highlight attachment (optional)"
              : "Remove highlight"}
          </h2>
          <p class="mb-8">
            ${highlight
              ? "This attachment will be highlighted on the published reporting page. Are you sure you want to highlight this attachment?"
              : "This attachment will no longer be highlighted on the published reporting page. It will still appear in the attachments section. Are you sure you want to remove this highlight?"}
          </p>
          <div class="flex justify-end items-center">
            <button
              type="button"
              @click=${onClose}
              class="border-px border-blue-200 rounded-md bg-blue-100 px-6 py-4 text-blue font-bold"
            >
              Cancel
            </button>
            <button
              type="button"
              @click=${onConfirm}
              class="ml-5 rounded-md bg-blue px-6 py-4 text-white font-bold"
            >
              ${highlight
                ? "Yes, feature this attachment"
                : "Yes, remove highlight"}
            </button>
          </div>
        </div>
      `,
    }),
    modalMount
  )
}

const bindDeleteButton = ($btn, $form) => {
  const $container = $form.closest(".attachments-container")

  const onDelete = () => {
    fetch($btn.dataset.delete, {
      method: "DELETE",
      headers: {
        "X-CSRFToken": csrfToken,
      },
    })
      .then((res) => {
        if (res.ok) {
          $container?.dispatchEvent(new CustomEvent("refresh"))
        } else {
          return res.text().then((text) => {
            throw new Error(text)
          })
        }
      })
      .catch((error) => {
        console.error(error)
        addToastMessage("There was an error deleting the attachment", "error")
      })
  }

  $btn.addEventListener("click", (e) => {
    e.stopPropagation()
    confirmDeleteAttachment($form, onDelete)
  })
}

document.addEventListener("htmx:afterSwap", (e) => {
  e.detail.elt
    .querySelectorAll("[data-action='nested-document-input']")
    .forEach(renderNestedDocumentFormInput)

  if (e.target.id === "attachments" || e.target.id === "reporting") {
    const $attachments = e.target
    renderImageGallery($attachments)

    const showMoreAttachments = ($innerEl) => {
      const $attachmentBlock =
        $innerEl.closest(".attachment-block") || $attachments
      $attachmentBlock
        .querySelectorAll("[id^='document-'].hidden")
        .forEach(($att) => {
          $att.classList.remove("hidden")
        })
      $attachmentBlock.querySelector("button.show-more-btn")?.remove()
    }

    const anchor = window.location.hash.substring(1)
    if (anchor.startsWith("document-")) {
      const $attachment = document.getElementById(anchor)
      if ($attachment !== null) {
        if ($attachment.classList.contains("hidden"))
          showMoreAttachments($attachment)
        $attachment.scrollIntoView({ behavior: "smooth", block: "center" })
        $attachment.classList.add("animate-[highlight_2s_ease-out]")
      }
    }

    $attachments.querySelectorAll("button.show-more-btn").forEach(($btn) => {
      $btn.addEventListener("click", (e) => {
        e.preventDefault()
        showMoreAttachments($btn)
      })
    })

    const downloadButtons = $attachments.querySelectorAll(
      "button[data-download]"
    )
    downloadButtons.forEach(bindDownloadButton)

    const shareButtons = $attachments.querySelectorAll("button[data-share]")
    shareButtons.forEach(bindShareButton)

    const highlightButtons = $attachments.querySelectorAll(
      "button[data-highlight]"
    )
    highlightButtons.forEach(($btn) => {
      bindHighlightButton($attachments, $btn)
    })

    const unhighlightButtons = $attachments.querySelectorAll(
      "button[data-unhighlight]"
    )
    unhighlightButtons.forEach(($btn) => {
      bindUnhighlightButton($attachments, $btn)
    })

    const $downloadAllBtn = $attachments.querySelector(
      "button[data-download-all]"
    )
    if ($downloadAllBtn) {
      if (downloadButtons.length < 2) {
        $downloadAllBtn.remove()
      } else {
        $downloadAllBtn.addEventListener("click", (e) => {
          e.preventDefault()
          downloadButtons.forEach(($btn, idx) => {
            setTimeout(() => {
              $btn.click()
            }, idx * 2000)
          })
        })
      }
    }
  } else if (e.target.id.startsWith("document-")) {
    const $attachmentRow = e.target
    const $attachments = $attachmentRow.closest("#attachments, #reporting")
    renderImageGallery($attachments)

    const downloadButtons = $attachmentRow.querySelectorAll(
      "button[data-download]"
    )
    downloadButtons.forEach(bindDownloadButton)

    const shareButtons = $attachmentRow.querySelectorAll("button[data-share]")
    shareButtons.forEach(bindShareButton)

    const highlightButtons = $attachmentRow.querySelectorAll(
      "button[data-highlight]"
    )
    highlightButtons.forEach(($btn) => {
      bindHighlightButton($attachments, $btn)
    })

    const unhighlightButtons = $attachmentRow.querySelectorAll(
      "button[data-unhighlight]"
    )
    unhighlightButtons.forEach(($btn) => {
      bindUnhighlightButton($attachments, $btn)
    })
  } else if (
    e.target.classList.contains("attachment-form") &&
    e.target.closest(".add-attachments") === null
  ) {
    const $attachmentForm = e.target
    $attachmentForm
      .querySelectorAll("button[data-delete]")
      .forEach(($btn) => bindDeleteButton($btn, $attachmentForm))
  }
})
